import { useEffect, useMemo, useState } from 'react';
import { makePostCall, PatientHeader } from '../../../utils/helper';
import { REPORT_CONFIG } from '../constants';
import type { Report, OrderDetails } from '../types';

/**
 * Fetches header/footer config from backend and generates header HTML for NEW reports.
 * Existing reports already have header/footer stored — this hook returns null for those.
 *
 * Used by both ReportEditor and SplitScreenEditor to eliminate duplicate logic.
 */
export const useHeaderFooter = (orderDetails: OrderDetails | null, currentReport: Report | null) => {
  const [footerTemplate, setFooterTemplate] = useState('');
  const [showFooter, setShowFooter] = useState(true);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadConfig = async () => {
      try {
        const response = await makePostCall('/no-auth/report-header-config', { action: 'get' });
        if (cancelled) return;

        const config = response?.data?.config;
        const shouldShowFooter = config ? config.showFooter !== false : true;
        setShowFooter(shouldShowFooter);

        const hasStoredFooter = !!(currentReport && currentReport.pr_report_footer);

        if (shouldShowFooter && !hasStoredFooter) {
          setFooterTemplate(config?.footerTemplate || REPORT_CONFIG.DEFAULT_FOOTER_TEXT);
        } else {
          setFooterTemplate('');
        }
      } catch {
        if (cancelled) return;
        setShowFooter(true);
        const hasStoredFooter = !!(currentReport && currentReport.pr_report_footer);
        setFooterTemplate(hasStoredFooter ? '' : REPORT_CONFIG.DEFAULT_FOOTER_TEXT);
      } finally {
        if (!cancelled) setConfigLoaded(true);
      }
    };

    loadConfig();
    return () => { cancelled = true; };
  }, [currentReport]);

  /** Header HTML — only generated for NEW reports (no stored header) */
  const headerHtml = useMemo(() => {
    if (!orderDetails) return null;
    if (currentReport && currentReport.pr_report_header) return null;
    const { patient } = orderDetails.pacs_order;
    return PatientHeader(patient, currentReport, orderDetails.pacs_order, orderDetails.assigned_radiologist_name);
  }, [orderDetails, currentReport]);

  /** Footer HTML — respects showFooter config, only for NEW reports */
  const footerHtml = useMemo(() => {
    if (!showFooter) return '';
    if (currentReport && currentReport.pr_report_footer) return '';
    return footerTemplate;
  }, [currentReport, showFooter, footerTemplate]);

  return { headerHtml, footerHtml, showFooter, configLoaded };
};
